<script setup lang="ts">
  import ModalPosTable from '@/components/modals/pos/ModalPosTable.vue';
  import { useModal } from '@/composition/use-modal';
  import {
    OrderPaymentStatus,
    OrderStatus,
    OrderType,
  } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { ITable } from '@/interfaces/ITable';
  import { IPosTableView, TPosTableStatus } from '@/interfaces/IPosTableView';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { orderMatchesSearch } from '@/utils/orders.utils';
  import { useLocalStorage } from '@vueuse/core';
  import { computed, getCurrentInstance, inject, Ref } from 'vue';
  import PosFloorPlan from './PosFloorPlan.vue';

  const modals = useModal();

  const restaurantStore = useRestaurantStore();
  const ordersStore = useOrdersStore();

  const search = inject<Ref<string | null>>('search')!;

  const tableStatuses: TPosTableStatus[] = [
    'empty',
    'pending',
    'finished',
    'paid',
  ];

  const tables = computed<IPosTableView[] | null>(() => {
    if (!restaurantStore.tables) return null;

    let tableOrders = ordersStore.pendingOrdersByType?.[OrderType.Seat] ?? [];

    if (search.value?.trim()) {
      tableOrders = tableOrders.filter((order) =>
        orderMatchesSearch(order, search.value!)
      );
    }

    const ordersByTableId = tableOrders.reduce((acc, order) => {
      if (!acc[order.tableId]) acc[order.tableId] = [];
      acc[order.tableId].push(order);
      return acc;
    }, {} as Record<string, IOrder[]>);

    const tables: IPosTableView[] = [];

    restaurantStore.tables.forEach((table) => {
      const orders = ordersByTableId[table.id] ?? [];

      if (!table.enabled && !orders.length) return;

      let total = 0;
      let tableStatusIndex: number = orders.length ? 3 : 0;

      const names: string[] = [];

      for (const order of orders) {
        total += order.total;

        if (order.userName) {
          names.push(order.userName);
        }

        if (tableStatusIndex > 1) {
          if (order.status !== OrderStatus.Finished) {
            tableStatusIndex = 1;
          } else if (order.paymentStatus !== OrderPaymentStatus.Paid) {
            tableStatusIndex = 2;
          }
        }

        /*if (tableStatus === 'paid') {
          if (order.paymentStatus !== OrderPaymentStatus.Paid || order.status !== OrderStatus.Finished) {
            tableStatus = 'finished';
          }
        }

        if (tableStatus === 'finished') {
          if (order.status !== OrderStatus.Finished) {
            tableStatus = 'pending';
          }
        }*/
      }

      let chipColor: string | undefined = undefined,
        chipTheme: string | undefined = undefined;

      const tableStatus = tableStatuses[tableStatusIndex];
      switch (tableStatus) {
        case 'empty':
          break;
        case 'pending':
          chipColor = 'orange';
          break;
        case 'finished':
          chipColor = 'blue';
          chipTheme = 'dark';
          break;
        case 'paid':
          chipColor = 'green';
          chipTheme = 'dark';
          break;
      }

      tables.push({
        ...table,
        orders,
        total,
        tableStatus,
        names,
        chipColor,
        chipTheme,
      });
    });

    return tables;
  });

  //#region Plan / list view

  const sectors = computed(() => restaurantStore.sectors ?? []);

  /** Plan available iff at least one table is placed in a live sector. */
  const planAvailable = computed(() => {
    if (!sectors.value.length || !restaurantStore.tables) return false;
    const liveSectorIds = new Set(sectors.value.map((s) => s.id));
    return restaurantStore.tables.some(
      (t) =>
        t.sectorId !== null &&
        t.posX !== null &&
        t.posY !== null &&
        liveSectorIds.has(t.sectorId)
    );
  });

  const viewPref = useLocalStorage<'auto' | 'plan' | 'list'>(
    'adminPosTablesView',
    'auto'
  );

  /**
   * `auto` resolves to plan-when-available (list otherwise); an explicit
   * "plan" pick is honored even without a plan so the pane can show the
   * "create your first floor plan" call to action.
   */
  const effectiveView = computed<'plan' | 'list'>(() => {
    if (viewPref.value === 'plan') return 'plan';
    if (viewPref.value === 'list') return 'list';
    return planAvailable.value ? 'plan' : 'list';
  });

  function setView(view: 'plan' | 'list') {
    viewPref.value = view;
  }

  //#endregion

  function openTable(table: ITable) {
    modals.show(
      // @ts-ignore
      ModalPosTable,
      {
        table,
        search,
      },
      {
        name: 'pos-table',
        classes: 'dialog--height-limit',
        adaptive: true,
        height: 700,
        width: 1000,
        maxWidth: window.innerWidth * 0.95,
        transition: 'none',
      },
      {}
    );
  }
</script>

<template>
  <div class="tables-container">
    <template v-if="!restaurantStore.tables">Loading...</template>
    <template v-else-if="!tables?.length">
      <p class="empty">
        No hay ninguna mesa disponible. Si quiere poder recibir o crear pedidos
        de mesas, asegúrese de
        <router-link :to="`/${restaurantStore.restaurant.stringId}/tables`"
          >crear algunas</router-link
        >
      </p>
    </template>
    <template v-else>
      <div class="view-toggle">
        <v-btn-toggle
          :value="effectiveView"
          mandatory
          dense
          class="toggle-group"
          @change="setView"
        >
          <v-btn small value="plan">
            <v-icon small class="tw-pr-1">mdi-floor-plan</v-icon>
            <span>{{ $t('posTables.viewPlan') }}</span>
          </v-btn>
          <v-btn small value="list">
            <v-icon small class="tw-pr-1">mdi-view-grid-outline</v-icon>
            <span>{{ $t('posTables.viewList') }}</span>
          </v-btn>
        </v-btn-toggle>
      </div>

      <PosFloorPlan
        v-if="effectiveView === 'plan' && planAvailable"
        :sectors="sectors"
        :tables="tables"
        @table-click="openTable"
      />
      <div v-else-if="effectiveView === 'plan'" class="plan-cta">
        <div class="cta-card">
          <div class="cta-icon">
            <v-icon color="primary">mdi-floor-plan</v-icon>
          </div>
          <v-chip x-small color="primary" outlined class="cta-badge">
            {{ $t('posTables.planCtaNew') }}
          </v-chip>
          <h3 class="cta-title">{{ $t('posTables.planCtaTitle') }}</h3>
          <p class="cta-description">{{ $t('posTables.planCtaText') }}</p>
          <v-btn
            color="primary"
            depressed
            :to="`/${restaurantStore.restaurant.stringId}/tables/plan`"
          >
            <v-icon small class="tw-mr-1">mdi-floor-plan</v-icon>
            {{ $t('posTables.planCtaButton') }}
          </v-btn>
        </div>
      </div>
      <div v-else class="tables">
        <div
          v-for="table in tables"
          :key="table.id"
          class="table"
          :class="`table--${table.tableStatus}`"
          @click="openTable(table)"
        >
          <div class="header">
            <div class="name-container">
              <v-icon small>mdi-table-furniture</v-icon>
              <span class="name">{{ table.name }}</span>
            </div>
            <v-chip
              v-if="table.tableStatus !== 'empty'"
              small
              class="chip order-count"
              :color="table.chipColor"
              :dark="table.chipTheme === 'dark'"
            >
              <v-icon small class="tw-pr-1">mdi-receipt</v-icon>
              {{ table.orders.length }}
              <v-icon
                v-if="
                  table.tableStatus === 'finished' ||
                  table.tableStatus === 'paid'
                "
                small
                class="tw-pl-1"
                >mdi-check-circle-outline</v-icon
              >
            </v-chip>
          </div>
          <div class="body">
            <div v-if="table.names.length" class="names">
              <v-icon small>mdi-account</v-icon>
              <span>{{ table.names.join(', ') }}</span>
            </div>
            <div v-else></div>
            <v-chip
              v-if="table.tableStatus !== 'empty'"
              small
              class="chip total"
              :color="table.chipColor"
              :dark="table.chipTheme === 'dark'"
            >
              <v-icon small class="tw-pr-1">mdi-cash</v-icon>
              {{ restaurantStore.currency?.symbol }}
              {{ displayPrice(table.total) }}
              <v-icon v-if="table.tableStatus === 'paid'" small class="tw-pl-1"
                >mdi-check-circle-outline</v-icon
              >
            </v-chip>
          </div>
        </div>
        <div class="edit-tables-container">
          <v-btn
            icon
            small
            color="grey"
            :to="`/${restaurantStore.restaurant.stringId}/tables`"
            class="edit-tables"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .tables-container {
    .empty {
      @apply tw-text-center tw-text-gray-500 tw-p-8 tw-pb-2;
    }

    .view-toggle {
      @apply tw-flex tw-justify-end tw-mb-3;

      .toggle-group {
        @apply tw-rounded-lg;
      }
    }

    .plan-cta {
      @apply tw-flex tw-items-center tw-justify-center tw-py-10;

      .cta-card {
        @apply tw-flex tw-flex-col tw-items-center tw-text-center;
        @apply tw-max-w-md tw-w-full;
        @apply tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-gray-300;
        @apply tw-bg-white tw-px-8 tw-py-10;

        .cta-icon {
          @apply tw-flex tw-items-center tw-justify-center;
          @apply tw-w-14 tw-h-14 tw-rounded-full tw-mb-3;
          @apply tw-bg-orange-50;
        }

        .cta-badge {
          @apply tw-mb-2;
        }

        .cta-title {
          @apply tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2;
        }

        .cta-description {
          @apply tw-text-sm tw-text-gray-500 tw-leading-relaxed tw-mb-5;
        }
      }
    }

    .tables {
      @apply tw-grid tw-gap-4 tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 2xl:tw-grid-cols-5;

      .table {
        @apply tw-py-3 tw-px-3 tw-rounded-md tw-bg-gray-100 tw-space-y-1 tw-cursor-pointer tw-h-[4.75rem];
        @apply tw-flex tw-flex-col tw-justify-between;

        .header {
          @apply tw-flex tw-justify-between tw-items-start;

          .name-container {
            @apply tw-flex tw-items-center tw-space-x-1;

            .name {
              @apply tw-truncate tw-flex-1;
              @apply tw-font-semibold tw-tracking-wide;
            }
          }
        }

        .chip {
          @apply tw-rounded-md;
        }

        .body {
          @apply tw-flex tw-justify-between tw-items-center tw-space-x-2;

          .names {
            @apply tw-flex-1 tw-min-w-1 tw-truncate tw-text-xs tw-space-x-1;
            line-height: 1.2em;

            & > * {
              @apply tw-align-middle;
            }
          }
        }

        &.table--empty {
        }

        &.table--pending {
          @apply tw-bg-orange-300;
        }

        &.table--finished {
          @apply tw-bg-blue-300;
        }

        &.table--paid {
          background-color: #75e1a2;
        }

        &:hover {
          // gray-150
          background-color: #ecedef;

          &.table--pending {
            // orange-350
            background-color: #ffae59;
          }

          &.table--finished {
            // blue-350
            background-color: #81bcff;
          }

          &.table--paid {
            // green-350
            background-color: #6fcf97;
          }
        }
      }

      .edit-tables-container {
        @apply tw-flex tw-items-center tw-justify-start;
      }
    }
  }
</style>
